// ── Color manipulation helpers ──────────────────────────────────────
const hexToRgb = (hex) => {
    const h = hex.replace('#', '');
    return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
};
const rgbToHex = (r, g, b) => '#' + [r, g, b].map(x => Math.max(0, Math.min(255, Math.round(x))).toString(16).padStart(2, '0')).join('');
const lighten = (hex, amount) => {
    const [r, g, b] = hexToRgb(hex);
    return rgbToHex(r + (255 - r) * amount, g + (255 - g) * amount, b + (255 - b) * amount);
};
const darken = (hex, amount) => {
    const [r, g, b] = hexToRgb(hex);
    return rgbToHex(r * (1 - amount), g * (1 - amount), b * (1 - amount));
};
// WCAG relative luminance and contrast ratio, for the readability checks.
const luminance = (hex) => {
    const [r, g, b] = hexToRgb(hex)
        .map(c => c / 255)
        .map(c => c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};
export const contrast = (a, b) => {
    const [light, dark] = [luminance(a), luminance(b)].sort((x, y) => y - x);
    return (light + 0.05) / (dark + 0.05);
};
// ── Core configurable colors (what admin picks) ─────────────────────
export const coreColorVars = {
    'Primary': '--color-primary',
    'Primary-Dark': '--color-primary-dark',
    'Primary-Light': '--color-primary-light',
    'Secondary': '--color-secondary',
    'Accent': '--color-accent',
    'Background': '--color-background',
    'Foreground': '--color-foreground',
    'Border': '--color-border',
};
export const colorNames = Object.keys(coreColorVars);
// The CSS-variable keys among a community's saved theme settings; the rest are display settings.
export const colorKeys = new Set(Object.values(coreColorVars));
export const themePresets = [
    {
        id: 'default',
        name: 'Default',
        description: 'Plain grey on white',
        colors: {
            'Primary': '#495057',
            'Primary-Dark': '#343a40',
            'Primary-Light': '#6c757d',
            'Secondary': '#adb5bd',
            'Accent': '#495057',
            'Background': '#ffffff',
            'Foreground': '#212529',
            'Border': '#dee2e6',
        },
    },
    {
        id: '76unboxed',
        name: '76UNBOXED',
        description: 'Our own navy and red',
        colors: {
            'Primary': '#40546b',
            'Primary-Dark': '#2e3e50',
            'Primary-Light': '#5c7088',
            'Secondary': '#5c7088',
            'Accent': '#a92425',
            'Background': '#ffffff',
            'Foreground': '#545c64',
            'Border': '#e0e2e4',
        },
    },
    {
        id: 'farmhouse',
        name: 'Farmhouse',
        description: 'Sage green and wheat on cream',
        colors: {
            'Primary': '#3f5e4a',
            'Primary-Dark': '#2d4436',
            'Primary-Light': '#4f6f5a',
            'Secondary': '#6b6457',
            'Accent': '#ebcd8f',
            'Background': '#fbf8f1',
            'Foreground': '#2f2b25',
            'Border': '#e4ddcf',
        },
    },
    {
        id: 'porch-light',
        name: 'Porch Light',
        description: 'Dark: rust and lamplight on charcoal',
        colors: {
            'Primary': '#a45b38',
            'Primary-Dark': '#7e4127',
            'Primary-Light': '#ab6240',
            'Secondary': '#b2a999',
            'Accent': '#fff3d6',
            'Background': '#1b1e22',
            'Foreground': '#ece5d8',
            'Border': '#3b4149',
        },
    },
];
// What a community or tour that never picked a theme shows.
export const defaultColors = themePresets[0].colors;
export const sameColors = (a, b) => colorNames.every(name => a[name].toLowerCase() === b[name].toLowerCase());
// The preset the colors are exactly, if any; colors that match none are Custom.
export const matchPreset = (colors, presets = themePresets) => { var _a; return (_a = presets.find(preset => sameColors(preset.colors, colors))) !== null && _a !== void 0 ? _a : null; };
// What a theme is called: the preset its colors are, or Custom.
export const themeNameOf = (colors, presets = themePresets) => { var _a, _b; return (_b = (_a = matchPreset(colors, presets)) === null || _a === void 0 ? void 0 : _a.name) !== null && _b !== void 0 ? _b : 'Custom'; };
// ── Basic picks ─────────────────────────────────────────────────────
// The picker's basic controls each set the colors they own and leave the rest, hand-set ones included.
// A new primary with its darker and lighter shades (hover states, the tour's bars and badges). The
// lighter one stops where white text on it would read worse than on the primary itself.
export const withPrimary = (core, primary) => {
    const floor = Math.min(4.5, contrast('#ffffff', primary));
    const light = [0.15, 0.1, 0.05].map(amount => lighten(primary, amount)).find(shade => contrast('#ffffff', shade) >= floor);
    return Object.assign(Object.assign({}, core), { 'Primary': primary, 'Primary-Dark': darken(primary, 0.25), 'Primary-Light': light !== null && light !== void 0 ? light : primary });
};
// The background, text, muted text and border Light or Dark sets.
const standardNeutrals = {
    light: { 'Background': '#ffffff', 'Foreground': '#212529', 'Secondary': '#6c757d', 'Border': '#dee2e6' },
    dark: { 'Background': '#1b1e22', 'Foreground': '#ece5d8', 'Secondary': '#adb5bd', 'Border': '#3b4149' },
};
// Already in the mode, the theme keeps its own background and text.
export const withMode = (core, mode) => colorModeOf(core) === mode ? core : Object.assign(Object.assign({}, core), standardNeutrals[mode]);
// Core colors from saved theme settings, the default theme filling any gap.
export const coreFromSettings = (settings) => {
    const map = new Map(settings.map(s => [s.key, s.value]));
    return Object.fromEntries(colorNames.map(name => [name, map.get(coreColorVars[name]) || defaultColors[name]]));
};
export const settingsFromCore = (core) => colorNames.map(name => ({ key: coreColorVars[name], value: core[name] }));
// ── Derive ALL CSS vars from the core colors ────────────────────────
// Bootstrap's color mode follows the background, so inputs, cards and scrollbars read against it.
export const colorModeOf = (core) => luminance(core.Background) < 0.4 ? 'dark' : 'light';
export const deriveAllCssVars = (core) => ({
    // Brand
    '--color-primary': core.Primary,
    '--color-primary-dark': core['Primary-Dark'],
    '--color-primary-light': core['Primary-Light'],
    '--color-secondary': core.Secondary,
    '--color-accent': core.Accent,
    // Base. The surface (panels, bands, and in dark mode the inputs) is the border lifted toward
    // white, which only suits a light background; on a dark one that comes out pale, so there it is
    // the background lifted a little instead.
    '--color-foreground': core.Foreground,
    '--color-background': core.Background,
    '--color-surface': colorModeOf(core) === 'dark' ? lighten(core.Background, 0.08) : lighten(core.Border, 0.6),
    '--color-border': core.Border,
    // Interactive — derived from primary
    '--color-link': core.Primary,
    '--color-link-hover': core['Primary-Dark'],
    '--color-button-bg': core.Primary,
    '--color-button-text': '#ffffff',
    // Semantic — standard, not per-district
    '--color-success': '#198754',
    '--color-warning': '#ffc107',
    '--color-danger': '#dc3545',
    '--color-info': '#0dcaf0',
    // Layout — derived from base
    '--color-header-bg': core.Background,
    '--color-header-text': core.Foreground,
    '--color-card-bg': core.Background,
    '--color-card-border': core.Border,
});
